import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../constants';
import { ExchangeRates, getExchangeRatesKey, useCurrencyStore } from '../stores/useCurrencyStore';
import { formatValue, isFailureResponse } from '../utils';
import { useUser } from './useController';
import { useStoreInformation } from './useStoreInformation';
import { StorePerformanceDailyRequest, useTransaction } from './useTransaction';

const fetchExchangeRatesPromisesMap: Record<string, Promise<void>> = {};

export type CurrencyFormatterOptions = Intl.NumberFormatOptions & {
  showCurrencyCode?: boolean;
  locale?: string;
};

export const useCurrencyTools = (interval?: StorePerformanceInterval, date?: Date) => {
  const { currencyCode: storeCurrency } = useStoreInformation();
  const { i18n } = useTranslation();
  const { fetchCurrencyConversionData } = useTransaction();
  const {
    get: { userPermissions },
  } = useUser();
  const {
    userCurrency,
    exchangeRatesMap,
    latestExchangeRatesKey,
    setLatestExchangeRatesKey,
    getExchangeRates,
    addExchangeRates,
  } = useCurrencyStore();

  const defaultLocale = i18n.language || 'en-US';
  const canViewCurrencyConverter = userPermissions?.currencyConverter?.canRead;

  const latestExchangeRates: ExchangeRates | null = useMemo(() => {
    const foundExchangeRatesEntry = Object.entries(exchangeRatesMap).find(
      (exchangeRatesEntry) => exchangeRatesEntry[0] === latestExchangeRatesKey,
    );

    const foundExchangeRates = foundExchangeRatesEntry ? foundExchangeRatesEntry[1] : null;

    if (!foundExchangeRates) return null;

    return foundExchangeRates;
  }, [latestExchangeRatesKey, exchangeRatesMap]);

  const exchangeRates = useMemo(() => {
    if (!canViewCurrencyConverter) return null;

    if (!interval || !date) return latestExchangeRates;

    return getExchangeRates(interval, date);
  }, [canViewCurrencyConverter, interval, date, exchangeRatesMap, latestExchangeRates]);

  const selectedCurrency = useMemo(() => {
    if (!exchangeRates || !userCurrency || !exchangeRates[userCurrency]) {
      return storeCurrency || null;
    }

    return userCurrency || storeCurrency || null;
  }, [userCurrency, storeCurrency, exchangeRates]);

  const getConvertedAmount = useCallback(
    (value: number): number => {
      const rate = exchangeRates && selectedCurrency ? exchangeRates[selectedCurrency] : 1;
      return rate && rate !== 0 ? value * (1 / rate) : value;
    },
    [selectedCurrency, exchangeRates],
  );

  const getFormattedCurrency = useCallback(
    (value: number, options?: CurrencyFormatterOptions): string => {
      const { showCurrencyCode = true, locale = defaultLocale, ...rest } = options || {};

      const convertedAmount = getConvertedAmount(value);

      if (!selectedCurrency) {
        return Number.parseFloat(value.toFixed(2)).toLocaleString();
      }
      const formattedAmount = formatValue(convertedAmount, {
        locale,
        options: {
          currency: selectedCurrency,
          ...rest,
        },
      });

      return showCurrencyCode ? `${formattedAmount} ${selectedCurrency}` : formattedAmount;
    },
    [selectedCurrency, exchangeRates],
  );

  const fetchExchangeRates = useCallback(
    async (ratesInterval: StorePerformanceInterval, ratesDate: Date) => {
      if (!ratesInterval || !ratesDate || !storeCurrency) return;

      const request = createStorePerformanceRequest({
        interval: ratesInterval,
        currentDate: ratesDate,
      });
      const fetchCurrencyConversionDataPromise = fetchCurrencyConversionData(
        storeCurrency,
        request as StorePerformanceDailyRequest,
      );

      const response = await fetchCurrencyConversionDataPromise;

      if (isFailureResponse(response)) {
        // remove the promise from the map
        delete fetchExchangeRatesPromisesMap[`${ratesInterval}_${ratesDate}`];
        return;
      }
      const REQUIRED_RATES_CODES = ['EUR', 'USD', 'SEK'];
      const filteredExchangeRates = Object.fromEntries(
        Object.entries(response.data.conversionMap).filter(([key]) => REQUIRED_RATES_CODES.includes(key)),
      );

      addExchangeRates(ratesInterval, ratesDate, filteredExchangeRates);
    },
    [storeCurrency],
  );

  useEffect(() => {
    if (!interval || !date) return;

    setLatestExchangeRatesKey(getExchangeRatesKey(interval as StorePerformanceInterval, date as Date));
    if (!canViewCurrencyConverter) return;

    const promisesMapKey = `${interval}_${date}`;

    const fetchExchangeRatesPromise = fetchExchangeRatesPromisesMap[promisesMapKey];

    if (!!fetchExchangeRatesPromise) return;

    fetchExchangeRatesPromisesMap[promisesMapKey] = fetchExchangeRates(
      interval as StorePerformanceInterval,
      date as Date,
    );
  }, [canViewCurrencyConverter, interval, date]);

  return {
    selectedCurrency,
    latestExchangeRates,
    getFormattedCurrency,
    getConvertedAmount,
  };
};
